/**
 * WeeklyLifeCard 組件
 *
 * 顯示每週生活建議的卡片組件，包含：
 * - 本週餐點建議（早餐、午餐、晚餐）
 * - 靜思反省項目
 * - 週末活動建議
 * - 紫微命理建議
 * - 節氣飲品推薦
 *
 * @module components/WeeklyLifeCard
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Button,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Restaurant as RestaurantIcon,
  SelfImprovement as ReflectionIcon,
  DirectionsRun as ActivityIcon,
  LocalCafe as DrinkIcon,
  StarBorder as StarIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  getWeeklyAdvice,
  WeeklyAdviceResponse,
  formatWeekRange,
  getWeekdayName,
  getActivityTypeName,
  hasWeeklyAdvice,
  getTotalMeals,
  getTotalActivities,
  getTotalReflections,
} from '../api/weeklylife';

/**
 * WeeklyLifeCard 組件屬性
 */
interface WeeklyLifeCardProps {
  /** 是否自動載入資料 */
  autoLoad?: boolean;
  /** 目標日期 (YYYY-MM-DD) */
  targetDate?: string;
  /** 載入完成回調 */
  onLoad?: (advice: WeeklyAdviceResponse) => void;
}

/**
 * WeeklyLifeCard 組件
 */
const WeeklyLifeCard: React.FC<WeeklyLifeCardProps> = ({
  autoLoad = true,
  targetDate,
  onLoad,
}) => {
  const [advice, setAdvice] = useState<WeeklyAdviceResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | false>(false);  // 修正：預設摺疊

  // 載入每週建議
  const loadWeeklyAdvice = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getWeeklyAdvice(targetDate);
      setAdvice(response);

      if (onLoad) {
        onLoad(response);
      }
    } catch (err: any) {
      console.error('載入每週建議失敗:', err);
      setError(err.message || '載入失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  }, [targetDate, onLoad]);

  // 自動載入
  useEffect(() => {
    if (autoLoad) {
      loadWeeklyAdvice();
    }
  }, [autoLoad, loadWeeklyAdvice]);

  // 處理 Accordion 展開
  const handleAccordionChange = (panel: string) => (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpanded(isExpanded ? panel : false);
  };

  // 渲染餐點項目
  const renderMealItem = (meal: any, dayIndex?: number) => {
    if (!meal) return null;

    // 映射不同類型餐點的欄位
    // 早餐格式: { id, drink, main_dish, estimated_kcal, tags }
    // 午餐/晚餐格式: { meal_id, meal_type, meal_name, components, estimated_kcal_avg, category }
    const mealName = meal.meal_name || meal.main_dish || meal.name || '未命名餐點';
    const mealDescription = meal.components || (meal.drink ? `搭配飲品: ${meal.drink}` : meal.description);
    const mealCalories = meal.estimated_kcal || meal.estimated_kcal_avg;

    return (
      <ListItem
        key={meal.id || meal.meal_id}
        sx={{
          flexDirection: 'column',
          alignItems: 'flex-start',
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          mb: 1,
          p: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, width: '100%' }}>
          {dayIndex !== undefined && (
            <Chip
              label={getWeekdayName(dayIndex)}
              size="small"
              color="primary"
              sx={{ mr: 1 }}
            />
          )}
          <Typography variant="subtitle1" fontWeight="bold">
            {mealName}
          </Typography>
          {mealCalories && (
            <Chip
              label={`${mealCalories} kcal`}
              size="small"
              variant="outlined"
              sx={{ ml: 'auto' }}
            />
          )}
        </Box>

        {mealDescription && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {mealDescription}
          </Typography>
        )}

        {meal.ingredients && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
            食材：{meal.ingredients}
          </Typography>
        )}

        {meal.nutrition_benefits && (
          <Typography variant="caption" color="success.main">
            營養價值：{meal.nutrition_benefits}
          </Typography>
        )}

        {meal.tags && (
          <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {meal.tags.split(';').map((tag: string, idx: number) => (
              <Chip key={idx} label={tag} size="small" variant="outlined" color="info" />
            ))}
          </Box>
        )}
      </ListItem>
    );
  };

  // 載入中狀態
  if (loading) {
    return (
      <Card>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  // 錯誤狀態
  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button variant="outlined" onClick={loadWeeklyAdvice} startIcon={<RefreshIcon />}>
            重新載入
          </Button>
        </CardContent>
      </Card>
    );
  }

  // 無資料狀態
  if (!advice || !hasWeeklyAdvice(advice)) {
    return (
      <Card>
        <CardContent>
          <Alert severity="info">
            本週尚未產生生活建議，請稍後再試
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="本週生活建議"
        subheader={
          advice.week_start && advice.week_end
            ? formatWeekRange(advice.week_start, advice.week_end)
            : ''
        }
        action={
          <IconButton onClick={loadWeeklyAdvice} title="重新整理">
            <RefreshIcon />
          </IconButton>
        }
      />

      <CardContent>
        {/* 統計資訊 */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            icon={<RestaurantIcon />}
            label={`${getTotalMeals(advice)} 份餐點`}
            size="small"
            variant="outlined"
          />
          <Chip
            icon={<ReflectionIcon />}
            label={`${getTotalReflections(advice)} 項反省`}
            size="small"
            variant="outlined"
          />
          <Chip
            icon={<ActivityIcon />}
            label={`${getTotalActivities(advice)} 項活動`}
            size="small"
            variant="outlined"
          />
        </Box>

        {/* 本週紫微命理建議 */}
        {advice.ziwei_advice &&
          advice.ziwei_advice.lowest_gong_name &&
          advice.ziwei_advice.lowest_gong_advice && (
            <Accordion
              expanded={expanded === 'ziwei'}
              onChange={handleAccordionChange('ziwei')}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <StarIcon sx={{ mr: 1 }} />
                <Typography>本週紫微命理建議</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Typography variant="subtitle2" color="error" gutterBottom>
                    本週運勢較弱宮位：{advice.ziwei_advice.lowest_gong_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {advice.ziwei_advice.lowest_gong_advice}
                  </Typography>
                </Box>
              </AccordionDetails>
            </Accordion>
          )}

        {/* 本週每日三餐建議 */}
        {advice.meals && (
          <Accordion
            expanded={expanded === 'meals'}
            onChange={handleAccordionChange('meals')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <RestaurantIcon sx={{ mr: 1 }} />
              <Typography>本週每日三餐建議</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {(() => {
                // 計算今天是星期幾，決定顯示順序
                const jsDayOfWeek = new Date().getDay(); // 0=日, 1=一, ..., 6=六
                const isWeekend = jsDayOfWeek === 0 || jsDayOfWeek === 6;
                // JS weekday 1~5 → array index 0~4
                const todayArrayIndex = isWeekend ? -1 : jsDayOfWeek - 1;
                const weekdayCount = advice.meals!.weekday_breakfasts?.length || 0;

                // 工作日順序：從今天開始排
                const weekdayIndices: number[] = [];
                for (let i = 0; i < weekdayCount; i++) {
                  weekdayIndices.push(
                    !isWeekend
                      ? (todayArrayIndex + i) % weekdayCount
                      : i
                  );
                }

                const mealLabels = ['早餐', '午餐', '晚餐'];
                const weekdaySources = [
                  advice.meals!.weekday_breakfasts,
                  advice.meals!.weekday_lunches,
                  advice.meals!.weekday_dinners,
                ];
                const weekendKeys: Array<keyof typeof advice.meals> = [
                  'weekend_breakfast', 'weekend_lunch', 'weekend_dinner',
                ];
                const hasWeekend = weekendKeys.some((k) => !!(advice.meals as any)[k]);

                const renderDaySection = (dayIdx: number, isToday: boolean) => {
                  const hasMeals = weekdaySources.some((src) => src?.[dayIdx]);
                  if (!hasMeals) return null;
                  return (
                    <Box key={dayIdx} sx={{ mb: 2 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Typography variant="subtitle2" fontWeight="bold" color="primary">
                          {getWeekdayName(dayIdx)}
                        </Typography>
                        {isToday && (
                          <Chip label="今天" size="small" color="success" />
                        )}
                      </Box>
                      <List dense disablePadding>
                        {weekdaySources.map((source, mIdx) => {
                          const meal = source?.[dayIdx];
                          if (!meal) return null;
                          return (
                            <Box key={mIdx}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ pl: 1, display: 'block' }}
                              >
                                {mealLabels[mIdx]}
                              </Typography>
                              {renderMealItem(meal)}
                            </Box>
                          );
                        })}
                      </List>
                      <Divider sx={{ mt: 1 }} />
                    </Box>
                  );
                };

                const renderWeekendSection = (isToday: boolean) => {
                  if (!hasWeekend) return null;
                  return (
                    <Box key="weekend" sx={{ mb: 2 }}>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Typography variant="subtitle2" fontWeight="bold" color="secondary">
                          週末
                        </Typography>
                        {isToday && (
                          <Chip label="今天" size="small" color="success" />
                        )}
                      </Box>
                      <List dense disablePadding>
                        {weekendKeys.map((key, mIdx) => {
                          const meal = (advice.meals as any)[key];
                          if (!meal) return null;
                          return (
                            <Box key={key as string}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ pl: 1, display: 'block' }}
                              >
                                {mealLabels[mIdx]}
                              </Typography>
                              {renderMealItem(meal)}
                            </Box>
                          );
                        })}
                      </List>
                    </Box>
                  );
                };

                return (
                  <>
                    {/* 週末時先顯示週末 */}
                    {isWeekend && renderWeekendSection(true)}

                    {/* 工作日（今天優先） */}
                    {weekdayIndices.map((dayIdx) =>
                      renderDaySection(dayIdx, dayIdx === todayArrayIndex)
                    )}

                    {/* 平日時週末顯示在最後 */}
                    {!isWeekend && renderWeekendSection(false)}
                  </>
                );
              })()}
            </AccordionDetails>
          </Accordion>
        )}

        {/* 節氣飲品推薦 */}
        {advice.solar_term && advice.solar_term.drink && (
          <Accordion
            expanded={expanded === 'drink'}
            onChange={handleAccordionChange('drink')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <DrinkIcon sx={{ mr: 1 }} />
              <Typography>節氣飲品推薦</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  當前節氣：{advice.solar_term.name}
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                  {advice.solar_term.drink.drink_name}
                </Typography>

                {advice.solar_term.drink.ingredients && (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    食材：{advice.solar_term.drink.ingredients}
                  </Typography>
                )}

                {advice.solar_term.drink.benefits && (
                  <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                    功效：{advice.solar_term.drink.benefits}
                  </Typography>
                )}

                {advice.solar_term.drink.preparation && (
                  <Typography variant="caption" color="text.secondary">
                    製作方式：{advice.solar_term.drink.preparation}
                  </Typography>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* 週末活動建議 */}
        {advice.activities &&
          (advice.activities.static_activity || advice.activities.dynamic_activity) && (
            <Accordion
              expanded={expanded === 'activities'}
              onChange={handleAccordionChange('activities')}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <ActivityIcon sx={{ mr: 1 }} />
                <Typography>週末活動建議</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {advice.activities.static_activity && (
                    <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Chip
                          label={getActivityTypeName('static')}
                          color="info"
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        <Typography variant="subtitle1" fontWeight="bold">
                          {advice.activities.static_activity.activity_name}
                        </Typography>
                      </Box>
                      {advice.activities.static_activity.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {advice.activities.static_activity.description}
                        </Typography>
                      )}
                      {advice.activities.static_activity.benefits && (
                        <Typography variant="caption" color="success.main">
                          益處：{advice.activities.static_activity.benefits}
                        </Typography>
                      )}
                    </ListItem>
                  )}

                  {advice.activities.dynamic_activity && (
                    <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Chip
                          label={getActivityTypeName('dynamic')}
                          color="warning"
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        <Typography variant="subtitle1" fontWeight="bold">
                          {advice.activities.dynamic_activity.activity_name}
                        </Typography>
                      </Box>
                      {advice.activities.dynamic_activity.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          {advice.activities.dynamic_activity.description}
                        </Typography>
                      )}
                      {advice.activities.dynamic_activity.benefits && (
                        <Typography variant="caption" color="success.main">
                          益處：{advice.activities.dynamic_activity.benefits}
                        </Typography>
                      )}
                    </ListItem>
                  )}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

        {/* 本週靜思重點 */}
        {advice.reflections && advice.reflections.length > 0 && (
          <Accordion
            expanded={expanded === 'reflections'}
            onChange={handleAccordionChange('reflections')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <ReflectionIcon sx={{ mr: 1 }} />
              <Typography>本週靜思重點</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {advice.reflections.map((reflection: any, index: number) => {
                  const reflectionText = reflection.question || '反省項目';
                  const category = reflection.category || '';

                  return (
                    <ListItem key={reflection.id} sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                      {category && (
                        <Chip label={category} size="small" color="secondary" sx={{ mb: 1 }} />
                      )}
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {index + 1}. {reflectionText}
                      </Typography>
                      {reflection.recommended_time && (
                        <Typography variant="caption" color="text.secondary">
                          建議時間：{reflection.recommended_time}
                        </Typography>
                      )}
                      {index < advice.reflections!.length - 1 && <Divider sx={{ mt: 1, width: '100%' }} />}
                    </ListItem>
                  );
                })}
              </List>
            </AccordionDetails>
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
};

export default WeeklyLifeCard;
